import Image from "next/image";
import RevenueChart from "./revenue-chart";
import { ChannelsCard, ImpactCard } from "./dashboard-cards";
import { useTranslation } from "@/lib/hooks/use-translation";

/**
 * Hero carousel slide 2 — the revenue dashboard open on a MacBook, with its
 * chart replayed live (see `revenue-chart.tsx`).
 *
 * The stage's aspect ratio is this screenshot's own (2388/1776), so the image
 * fills it edge to edge with no letterboxing. That is a requirement, not a
 * convenience: the chart overlay is positioned in the screenshot's pixel
 * space, so any slack between the image box and the stage would knock it out
 * of registration.
 *
 * The screenshot is cropped on its right and bottom: the laptop's own frame is
 * only in the shot at the top and left, and the dashboard runs off the other
 * two sides. Left as-is those cuts read as a broken image, so each is
 * dissolved with a gradient mask — the screenshot fades into the page rather
 * than being sliced by it, which also means the composition needs no
 * particular viewport width to look deliberate.
 *
 * The two masks sit on nested elements rather than in one `mask-composite`
 * rule, which is the one part of this that would need a fallback in older
 * Safari. The thresholds clear the live chart (its plot ends 79.6% down and
 * runs to the right edge): the bottom fade starts below the legend at 87%, and
 * the right fade at 93% only softens where the curves exit — which is where
 * the real chart is cut off anyway.
 */
export default function RevenueDashboard() {
  const { t } = useTranslation();

  return (
    <div className="relative w-full lg:flex lg:h-full lg:items-center">
      <div
        className="w-full lg:ml-auto lg:w-[84%]"
        style={{
          maskImage: "linear-gradient(to bottom, #000 87%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, #000 87%, transparent 100%)",
        }}
      >
        <div
          className="relative w-full"
          style={{
            maskImage: "linear-gradient(to right, #000 93%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to right, #000 93%, transparent 100%)",
          }}
        >
          <Image
            src="/v2/hero-macbook.png"
            alt={t("home.mocks.alt.macbook")}
            width={2388}
            height={1776}
            quality={90}
            sizes="(max-width: 1023px) 96vw, 50vw"
            className="h-auto w-full"
          />
          <RevenueChart />
        </div>
      </div>

      {/* Floating stat cards, per the comp: revenue channels off the top-right
          corner of the screen, discount/refund impact over the bottom-left.
          Outside the edge masks on purpose — they float above the screenshot,
          so they should not fade with it. Widths in % of the stage keep them
          proportional; jd-float keeps them adrift once landed.

          They are sized by `transform: scale`, not by width, and that is the
          whole trick. Their type is fixed px (down to 7px), so a percentage
          width does not shrink a card — it squeezes it, the Thai headings
          wrap to four lines and the card grows *taller* instead. Each one
          therefore keeps a fixed intrinsic width and scales as a unit, which
          preserves its internal layout exactly at any size.

          The scale lives on a third element, inside the entrance wrapper and
          the bob: all three write `transform`, so they each need their own
          element or the last one wins. Transform does not affect layout, so
          the visual box shrinks toward the `origin-*` corner while the
          absolute anchor stays put — which is why each card is anchored on
          the corner it scales into.

          Below md they sit in opposite corners (channels top-right, impact
          bottom-left) to keep a clear diagonal between them on the narrowest
          stage; from md they take the comp's own placement. */}
      <div
        className="hero-piece-right absolute right-[-3%] top-[-1%] z-[2] w-[260px] md:right-0 md:top-[4%] lg:top-[10%]"
        style={{ animationDelay: "0.5s" }}
      >
        <div className="jd-float" style={{ animationDelay: "-1.6s" }}>
          <div className="origin-top-right scale-[0.52] sm:scale-[0.8] md:scale-100">
            <ChannelsCard />
          </div>
        </div>
      </div>
      <div
        className="hero-piece-left absolute bottom-[-1%] left-[-4%] z-[2] w-[244px] md:bottom-[2%] md:left-[-20%] lg:bottom-auto lg:left-[10%] lg:top-[56%]"
        style={{ animationDelay: "0.62s" }}
      >
        <div className="jd-float" style={{ animationDelay: "-2.8s" }}>
          <div className="origin-bottom-left scale-[0.52] sm:scale-[0.8] md:scale-100">
            <ImpactCard />
          </div>
        </div>
      </div>
    </div>
  );
}
